/**
 * The live event stream — ONE per signed-in session (hand-off README rule 4).
 *
 *   POST /v1/events/stream-ticket  → a single-use ticket (the token never rides in a URL)
 *   GET  /v1/events/stream?ticket= → server-sent events, one NAMED event per platform event
 *
 * Both calls come from @inferrex/api-client (mintEventStreamTicket / eventStreamUrl), so the app
 * holds no API paths. The server sends `event: <type>` frames, which a browser delivers only to
 * `addEventListener(type)` — never to `onmessage` — so the stream registers a listener for every
 * type somebody subscribes to, including types added after it opened.
 *
 * Reconnects with capped, jittered backoff; a connection only counts as recovered after it has
 * held open for a while (an edge that opens then resets would otherwise loop at the base delay).
 * After a run of failures it stops and waits for the browser to come back online or the tab to
 * become visible. Behaviour reference: frontend/src/lib/sse-client.ts (the 2026-06-30 reconnect
 * storm is why the ceiling and the stability window exist).
 */

/** One event as the API relays it (services/api/src/routes/events.ts). */
export interface LiveEvent {
  readonly type: string;
  readonly correlationId?: string | undefined;
  readonly timestamp?: string | undefined;
  readonly payload?: Readonly<Record<string, unknown>> | undefined;
  readonly pipelineId?: string | undefined;
  readonly connectionId?: string | undefined;
}

export type LiveEventHandler = (event: LiveEvent) => void;
export type StreamState = 'live' | 'connecting' | 'offline';

/** The slice of EventSource the stream uses — a test passes a fake. */
export interface EventSourceLike {
  onopen: ((ev: Event) => unknown) | null;
  onerror: ((ev: Event) => unknown) | null;
  addEventListener(type: string, listener: (ev: MessageEvent) => void): void;
  close(): void;
}

export interface EventStreamDeps {
  readonly mintTicket: () => Promise<{ readonly ticket: string }>;
  readonly streamUrl: (ticket: string) => string;
  readonly createSource: (url: string) => EventSourceLike;
  /** Jitter source; tests pass a constant. */
  readonly random?: (() => number) | undefined;
  /** Where to listen for "back online" / "tab visible" after giving up. */
  readonly wakeTarget?: Pick<Window, 'addEventListener' | 'removeEventListener'> | undefined;
}

export const STREAM_BASE_DELAY_MS = 1_000;
export const STREAM_MAX_DELAY_MS = 30_000;
export const STREAM_MAX_ATTEMPTS = 12;
export const STREAM_STABLE_MS = 10_000;

export function reconnectDelay(attempt: number, random: () => number = Math.random): number {
  const ceiling = Math.min(STREAM_BASE_DELAY_MS * 2 ** attempt, STREAM_MAX_DELAY_MS);
  return ceiling / 2 + random() * (ceiling / 2);
}

function parse(data: unknown, fallbackType: string): LiveEvent | null {
  if (typeof data !== 'string') return null;
  try {
    const raw = JSON.parse(data) as Partial<LiveEvent> | null;
    if (!raw || typeof raw !== 'object') return null;
    return { ...raw, type: typeof raw.type === 'string' ? raw.type : fallbackType };
  } catch {
    return null;
  }
}

export class EventStream {
  private source: EventSourceLike | null = null;
  private readonly handlers = new Map<string, Set<LiveEventHandler>>();
  private readonly listening = new Set<string>();
  private readonly stateListeners = new Set<(s: StreamState) => void>();
  private attempt = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private stableTimer: ReturnType<typeof setTimeout> | null = null;
  private wake: (() => void) | null = null;
  private stopped = true;
  private generation = 0;
  private current: StreamState = 'offline';

  constructor(private readonly deps: EventStreamDeps) {}

  get state(): StreamState {
    return this.current;
  }

  onState(listener: (s: StreamState) => void): () => void {
    this.stateListeners.add(listener);
    return () => { this.stateListeners.delete(listener); };
  }

  /** Listen for one event type. Returns the unsubscribe. */
  subscribe(type: string, handler: LiveEventHandler): () => void {
    let set = this.handlers.get(type);
    if (!set) {
      set = new Set();
      this.handlers.set(type, set);
    }
    set.add(handler);
    this.listen(type);
    return () => { set.delete(handler); };
  }

  /** Open the stream. Safe to call again: it replaces the current connection. */
  async connect(): Promise<void> {
    this.stopped = false;
    this.closeSource();
    this.clearTimers();
    const generation = ++this.generation;
    this.setState('connecting');
    let url: string;
    try {
      const { ticket } = await this.deps.mintTicket();
      if (!ticket) throw new Error('No stream ticket');
      url = this.deps.streamUrl(ticket);
    } catch {
      if (generation === this.generation && !this.stopped) this.scheduleReconnect();
      return;
    }
    // A disconnect() or newer connect() while the ticket was minting wins.
    if (generation !== this.generation || this.stopped) return;

    const source = this.deps.createSource(url);
    this.source = source;
    this.listening.clear();
    source.onopen = () => {
      this.setState('live');
      if (this.stableTimer) clearTimeout(this.stableTimer);
      this.stableTimer = setTimeout(() => { this.attempt = 0; }, STREAM_STABLE_MS);
    };
    source.onerror = () => {
      if (this.source !== source) return;
      this.closeSource();
      this.scheduleReconnect();
    };
    source.addEventListener('message', (ev) => this.deliver(ev, 'message'));
    for (const type of this.handlers.keys()) this.listen(type);
  }

  /** Close the stream for good (sign-out, session end). */
  disconnect(): void {
    this.stopped = true;
    this.generation++;
    this.closeSource();
    this.clearTimers();
    this.clearWake();
    this.setState('offline');
  }

  private listen(type: string): void {
    if (!this.source || this.listening.has(type)) return;
    this.listening.add(type);
    this.source.addEventListener(type, (ev) => this.deliver(ev, type));
  }

  private deliver(ev: MessageEvent, type: string): void {
    const event = parse(ev.data, type);
    if (!event) return;
    for (const handler of this.handlers.get(event.type) ?? []) {
      try {
        handler(event);
      } catch {
        // One page's handler failing must not stop the others hearing the event.
      }
    }
  }

  private scheduleReconnect(): void {
    if (this.stopped) return;
    if (this.stableTimer) { clearTimeout(this.stableTimer); this.stableTimer = null; }
    if (this.attempt >= STREAM_MAX_ATTEMPTS) {
      this.setState('offline');
      this.armWake();
      return;
    }
    this.setState('connecting');
    const delay = reconnectDelay(this.attempt, this.deps.random);
    this.attempt += 1;
    this.reconnectTimer = setTimeout(() => { void this.connect(); }, delay);
  }

  private armWake(): void {
    const target = this.deps.wakeTarget ?? (typeof window !== 'undefined' ? window : undefined);
    if (!target || this.wake) return;
    const wake = () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      this.clearWake();
      this.attempt = 0;
      void this.connect();
    };
    this.wake = wake;
    target.addEventListener('online', wake);
    target.addEventListener('visibilitychange', wake);
  }

  private clearWake(): void {
    const target = this.deps.wakeTarget ?? (typeof window !== 'undefined' ? window : undefined);
    if (!this.wake || !target) return;
    target.removeEventListener('online', this.wake);
    target.removeEventListener('visibilitychange', this.wake);
    this.wake = null;
  }

  private closeSource(): void {
    if (!this.source) return;
    const s = this.source;
    this.source = null;
    s.onopen = null;
    s.onerror = null;
    s.close();
  }

  private clearTimers(): void {
    if (this.reconnectTimer) { clearTimeout(this.reconnectTimer); this.reconnectTimer = null; }
    if (this.stableTimer) { clearTimeout(this.stableTimer); this.stableTimer = null; }
  }

  private setState(next: StreamState): void {
    if (next === this.current) return;
    this.current = next;
    for (const l of this.stateListeners) l(next);
  }
}
